import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { MessageSquare, Users, Gamepad2, Inbox as InboxIcon, Eye, UserSearch, Home, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { xboxApiService } from '@/services/xboxApi';

let FRIENDS_POLLER_STARTED = false;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [onlineCount, setOnlineCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const computeFromCache = (): number => {
      try {
        const cached = localStorage.getItem('friends_cache');
        if (!cached) return 0;
        const list = JSON.parse(cached) as Array<any>;
        return list.filter((friend: any) =>
          (friend?.presenceState || '').toLowerCase() === 'online' || !!friend?.isOnline
        ).length;
      } catch {
        return 0;
      }
    };

    const fetchAndUpdate = async () => {
      try {
        const response = await xboxApiService.getFriendsViaProxy();
        if (response.data?.people) {
          localStorage.setItem('friends_cache', JSON.stringify(response.data.people));
          setOnlineCount(response.data.people.filter((person: any) =>
            (person?.presenceState || '').toLowerCase() === 'online'
          ).length);
          return;
        }
      } catch {}
      setOnlineCount(computeFromCache());
    };

    setOnlineCount(computeFromCache());
    if (!FRIENDS_POLLER_STARTED) {
      FRIENDS_POLLER_STARTED = true;
      if (!localStorage.getItem('friends_cache')) fetchAndUpdate();
      window.setInterval(fetchAndUpdate, 10000);
    }
    return () => undefined;
  }, []);

  const navItems = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/party-chats', label: 'Party Chats', icon: MessageSquare },
    { to: '/games', label: 'Game Sessions', icon: Gamepad2 },
    { to: '/friends', label: 'Friends', icon: Users, badge: onlineCount },
    { to: '/inbox', label: 'Inbox', icon: InboxIcon },
    { to: '/presence', label: 'Presence', icon: Eye },
    { to: '/profile-inspector', label: 'Profile Inspector', icon: UserSearch },
  ];

  const renderNav = (mobile = false) => navItems.map(({ to, label, icon: Icon, badge }) => (
    <NavigationMenuItem key={to} className={mobile ? 'w-full' : undefined}>
      <NavigationMenuLink asChild>
        <Link
          to={to}
          aria-current={location.pathname === to ? 'page' : undefined}
          className={cn(
            navigationMenuTriggerStyle(),
            mobile && 'h-11 w-full justify-start',
            location.pathname === to && 'bg-accent text-accent-foreground'
          )}
        >
          <Icon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">{label}</span>
          {!!badge && (
            <span className="ml-auto rounded-md bg-green-500/15 px-1.5 py-0.5 text-xs tabular-nums text-green-400">
              {badge}
            </span>
          )}
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  ));

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-16 items-center justify-between gap-4">
            <Link to="/" className="text-lg font-semibold tracking-tight text-foreground sm:text-xl">
              OctoSniff <span className="text-green-400">Xbox</span>
            </Link>

            <NavigationMenu className="hidden xl:flex">
              <NavigationMenuList>{renderNav()}</NavigationMenuList>
            </NavigationMenu>

            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring xl:hidden"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <NavigationMenu className="block max-w-none pb-4 xl:hidden">
              <NavigationMenuList className="grid w-full grid-cols-1 gap-1 space-x-0 sm:grid-cols-2 lg:grid-cols-3">
                {renderNav(true)}
              </NavigationMenuList>
            </NavigationMenu>
          )}
        </div>
      </header>

      <main id="main-content" className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;
